import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import SendIcon from '@mui/icons-material/Send';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import { CHAT_COMPOSER_PANEL_SX, CHAT_LIGHT_FIELD_SX, CHAT_NOTE_FIELD_SX } from '../chatComposerStyles';
import TypingIndicator from '../TypingIndicator';
import QuickRepliesPicker from '../QuickRepliesPicker';
import ChatSessionStatusMenu from '../ChatSessionStatusMenu';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

/**
 * Enterprise-style composer shell for the center stage column.
 * Core send / typing logic is wired; advanced controls are visual placeholders.
 */
export default function ChatStageComposer({
  canReply = false,
  replyText = '',
  onReplyChange,
  onSendReply,
  sending = false,
  markingSession = false,
  actionError = '',
  visitorTyping = false,
  visitorTypingLabel = 'Visitor is typing…',
  quickReplies = [],
  onQuickReply,
  currentStatus = '',
  onMarkAs,
}) {
  const [composerMode, setComposerMode] = useState('message');
  const isNoteMode = composerMode === 'note';

  if (!canReply) return null;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSendReply?.(e, { isInternal: isNoteMode });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={CHAT_COMPOSER_PANEL_SX}>
      {visitorTyping ? (
        <Box sx={{ px: 2, pt: 1, color: '#64748b' }}>
          <TypingIndicator label={visitorTypingLabel} />
        </Box>
      ) : null}

      <Box sx={{ px: 2, py: 1.5 }}>
        {actionError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {actionError}
          </Alert>
        ) : null}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1 }}
        >
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="composer-mode-label">Type</InputLabel>
            <Select
              labelId="composer-mode-label"
              label="Type"
              value={composerMode}
              onChange={(e) => setComposerMode(e.target.value)}
              disabled={sending || markingSession}
            >
              <MenuItem value="message">Message</MenuItem>
              <MenuItem value="note">Note</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Translate (coming soon)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TranslateOutlinedIcon fontSize="small" />}
                disabled
                sx={{ textTransform: 'none', minWidth: 56 }}
              >
                EN
              </Button>
            </span>
          </Tooltip>

          <QuickRepliesPicker
            replies={quickReplies}
            disabled={sending || markingSession || isNoteMode}
            onSelect={onQuickReply}
          />

          <Box sx={{ flex: 1 }} />

          <ChatSessionStatusMenu
            currentStatus={currentStatus}
            disabled={sending}
            loading={markingSession}
            onMarkAs={onMarkAs}
          />
        </Stack>

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          maxRows={6}
          placeholder={
            isNoteMode
              ? 'Add an internal note (not visible to visitor)…'
              : 'Type your reply to the visitor…'
          }
          value={replyText}
          onChange={onReplyChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          disabled={sending || markingSession}
          inputProps={{ 'aria-label': isNoteMode ? 'Internal note' : 'Agent reply' }}
          sx={isNoteMode ? CHAT_NOTE_FIELD_SX : CHAT_LIGHT_FIELD_SX}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Attach file (coming soon)">
              <span>
                <IconButton size="small" disabled aria-label="Attach file">
                  <AttachFileOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Emoji (coming soon)">
              <span>
                <IconButton size="small" disabled aria-label="Insert emoji">
                  <EmojiEmotionsOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Predefined answers (coming soon)">
              <span>
                <IconButton size="small" disabled aria-label="Predefined answers">
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.disabled', px: 0.5 }}
                  >
                    /
                  </Typography>
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            color={isNoteMode ? 'warning' : 'primary'}
            endIcon={isNoteMode ? <SaveOutlinedIcon fontSize="small" /> : <SendIcon fontSize="small" />}
            disabled={sending || markingSession || !replyText.trim()}
            sx={{ minWidth: 108, borderRadius: 2 }}
          >
            {sending ? (isNoteMode ? 'Saving…' : 'Sending…') : isNoteMode ? 'Save Note' : 'Send'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
